import {
  AlertMessage,
  Column,
  ComponentType,
  convertDateToString,
  convertISOStringToLocalDateString,
  createTableCell,
  createTableColumn,
  FieldValues,
  FilesDisplay,
  FormErrorDisplay,
  FormFieldMethods,
  Icon,
  ToastMessage,
  toastMessage,
  Tooltip,
} from '@gcl/react-core';
import * as React from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getFilenameAndExt } from '@core/Primitives/text.services';
import { useProjectId } from '@config/config.hooks';
import { QueryKey, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { useMutationWithOnSuccessOnError } from '@common/useMutationWithOnSuccessOnError.hooks';
import { AxiosErrorResponse } from '@core/error.models';
import { EProjectFileType } from './projectFile/projectFile.constants';
import {
  EProjectFileCategory,
  IFormFileAttachmentItem,
  IProjectFileBase,
  IProjectFileProcessResponse,
  ITempProjectFile,
  TFileDisplay,
} from './projectFile/projectFile.models';
import { FileUploadWithDisplayLoadingColumn } from './FileUploadWithDisplayLoadingColumn';
import './FileUploadWithDisplay.scss';
import { DownloadFile } from './DownloadFile';
import { useDeleteProjectFileAsync } from './projectFile/projectFile.queries';
import { IUploadProjectFileAsyncConfigProps } from './projectFile/projectFile.services';

export interface IFileUploadWithDisplayProps {
  defaultIsExpanded?: boolean;
  defaultFiles?: IProjectFileBase[];
  id: React.Key;
  header?: string;
  className?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  fileType?: EProjectFileType;
  uploadFileRequestParam?: { [key: string]: any };
  useFetchFilesQueryKey?: QueryKey;
  mode: DetailPageAccessMode;
  formFieldMethods?: FormFieldMethods<FieldValues, any>;
  formKeyToUpdateInCreateMode?: string;
  formKeyToUpdateInUpdateMode?: string;
  generateUseUploadProjectFileAsync?: (
    configProps: IUploadProjectFileAsyncConfigProps,
  ) => UseMutationResult<IProjectFileProcessResponse, AxiosErrorResponse, any, unknown>;
  customHeaderButtons?: React.ReactElement[];
  onUploadStatusChanged?: (uuid: string, isUploading: boolean) => void;
  shouldHaveAtLeastOneFile?: boolean;
  hasUploadRight?: boolean;
  hasDownloadRight?: boolean;
  shouldDisplayFileNameWhileNoAccessRight?: boolean;
  acceptFileType?: string;
  setIsAnyRowUploading?: React.Dispatch<React.SetStateAction<boolean>>;
  isAnyRowUploading?: boolean;
  type: TFileDisplay;
}
export const FileUploadWithDisplay: React.FunctionComponent<IFileUploadWithDisplayProps> = ({
  defaultIsExpanded = true,
  defaultFiles,
  id,
  header,
  className,
  onClick,
  fileType,
  uploadFileRequestParam = {},
  useFetchFilesQueryKey,
  mode,
  formFieldMethods,
  formKeyToUpdateInCreateMode,
  formKeyToUpdateInUpdateMode,
  generateUseUploadProjectFileAsync = undefined,
  customHeaderButtons = [],
  onUploadStatusChanged,
  shouldHaveAtLeastOneFile = false,
  hasUploadRight = true,
  hasDownloadRight = true,
  shouldDisplayFileNameWhileNoAccessRight = false,
  acceptFileType = undefined,
  setIsAnyRowUploading,
  isAnyRowUploading,
  type,
}) => {
  const projectId = useProjectId();
  const queryClient = useQueryClient();

  const refRowUploading = React.useRef<boolean[]>([]);
  const setRowUploading = (index: number, isUploading: boolean) => {
    const temp = [...refRowUploading.current];
    temp[index] = isUploading;
    refRowUploading.current = temp;
    const isAnyRowUploadingLocal = temp.find((item) => item === true) ?? false;
    if (setIsAnyRowUploading) setIsAnyRowUploading(isAnyRowUploadingLocal);
  };

  const [localFiles, setLocalFiles] = React.useState<IFormFileAttachmentItem[]>([]);
  const controlError = React.useMemo(() => {
    if (!formFieldMethods || !formKeyToUpdateInCreateMode) return undefined;
    return formFieldMethods.getFieldState(formKeyToUpdateInCreateMode).error;
  }, [JSON.stringify(formFieldMethods?.formState.errors), formKeyToUpdateInCreateMode]);

  const { mutate: mutateDeleteProjectFile, error: errorDeleteProjectFile } = useMutationWithOnSuccessOnError(
    useDeleteProjectFileAsync,
    (variables) => {
      toastMessage(
        <ToastMessage
          message="Delete Project File Success"
          toastOptions={{ type: 'success', closeOnClick: true, autoClose: 3000 }}
        />,
      );
      setLocalFilesWithShouldDirty((old) => {
        return old.filter((oldItem) => oldItem.form?.id !== variables?.id);
      });
      if (useFetchFilesQueryKey) queryClient.invalidateQueries(useFetchFilesQueryKey);
    },
    undefined,
    true,
  );

  React.useEffect(() => {
    setLocalFilesWithShouldDirty((oldFiles) => {
      if (oldFiles.length <= 0) {
        return (defaultFiles ?? []).map((defaultFile) => {
          const attachmentItem: IFormFileAttachmentItem = {
            fileInfo: defaultFile,
            form: { ...defaultFile, isTemp: mode === 'Create' },
            extension: '',
          };
          return attachmentItem;
        });
      }
      return oldFiles.map((oldFile) => {
        const found = defaultFiles?.find((defaultFile) => defaultFile?.uuid === oldFile?.form?.uuid);

        if (found) {
          const attachmentItem: IFormFileAttachmentItem = {
            fileInfo: found,
            form: { ...found, isTemp: mode === 'Create' },
            extension: '',
          };
          return attachmentItem;
        }
        return oldFile;
      });
    }, false);
  }, [JSON.stringify(defaultFiles), mode]);

  const setLocalFilesWithShouldDirty = (
    value: (old: IFormFileAttachmentItem[]) => IFormFileAttachmentItem[],
    shouldDirty = true,
  ) => {
    setLocalFiles((oldValue) => {
      const newValue = value(oldValue);
      if (
        (mode === 'Create' && formFieldMethods && formKeyToUpdateInCreateMode) ||
        (mode === 'Update' && formFieldMethods && formKeyToUpdateInUpdateMode)
      ) {
        if (mode === 'Create' && formKeyToUpdateInCreateMode) {
          const data = newValue.map((f) => {
            return {
              projectId,
              id: f.form?.id,
              isTemp: f.form?.isTemp,
              uuid: f.form?.uuid,
              fileName: f.form?.fileName,
              rowVersion: f.form?.rowVersion,
            } as ITempProjectFile;
          });
          formFieldMethods.setValue(formKeyToUpdateInCreateMode, data, { shouldDirty });
        }
        if (mode === 'Update' && formKeyToUpdateInUpdateMode) {
          const data = newValue.map((f) => {
            return f.form;
          });

          formFieldMethods.setValue(formKeyToUpdateInUpdateMode, data, { shouldDirty });
        }
      }

      return newValue;
    });
  };

  const attachmentColumns = React.useMemo(() => {
    const cols: Column[] = [
      createTableColumn({
        Header: '',
        accessor: 'id2',
        width: 30,
        disableResizing: true,
        disableSortBy: true,
        Cell: createTableCell<string, any>(
          (
            value: string,
            item: IFormFileAttachmentItem,
            index: number,
            cellExtras: { [key: string]: any },
            rowExtras,
          ) => {
            return (
              <FileUploadWithDisplayLoadingColumn
                type={type}
                useFetchFilesQueryKey={useFetchFilesQueryKey}
                rowExtras={rowExtras}
                item={item}
                setLocalFiles={setLocalFilesWithShouldDirty}
                mode={cellExtras.mode}
                generateUseUploadProjectFileAsync={generateUseUploadProjectFileAsync}
                onUploadStatusChanged={onUploadStatusChanged}
                index={index}
                setRowUploading={setRowUploading}
              />
            );
          },
        ),
      } as Column),
    ];
    if (mode !== 'View') {
      cols.push(
        createTableColumn({
          Header: '',
          accessor: 'id',
          width: 30,
          disableResizing: true,
          disableSortBy: true,
          Cell: createTableCell<string, any>(
            (value: string, item: IFormFileAttachmentItem, index: number, cellExtras: { [key: string]: any }) => {
              if (!item.fileInfo) return null;
              const { isAnyRowUploading: isAnyRowUploadingLocal } = cellExtras;
              return (
                <Tooltip
                  overlay={`Delete ${item?.form?.fileName || 'File'}`}
                  id={`gcl-gems-file-upload-delete-${item.form?.id}-tooltip`}
                >
                  <Icon
                    disabled={isAnyRowUploadingLocal === true}
                    iconName="Trash"
                    onClick={() => {
                      if (cellExtras.shouldHaveAtLeastOneFile && cellExtras.localFiles.length === 1) {
                        toastMessage(
                          <ToastMessage
                            message="At least one file is required, please upload a new file before deleting the last one."
                            toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
                          />,
                        );
                        return;
                      }
                      onClickItemDelete(item, index);
                    }}
                    className={isAnyRowUploadingLocal === true ? 'gcl-ic-md' : 'gcl-ic-btn-md'}
                  />
                </Tooltip>
              );
            },
          ),
        } as Column),
      );
    }
    cols.push(
      createTableColumn({
        Header: 'Date',
        accessor: 'form.lastModifiedDate',
        maxWidth: 200,
        minWidth: 100,
        Cell: createTableCell<string, any>(
          (value: string, item: IFormFileAttachmentItem, index: number, cellExtras: { [key: string]: any }) => {
            if (item.fileInfo?.lastModifiedDateTime)
              return <div>{convertISOStringToLocalDateString(item.fileInfo?.lastModifiedDateTime)}</div>;

            if (item.form?.lastModifiedDate)
              return <div>{convertDateToString(item.form?.lastModifiedDate ?? new Date())}</div>;
            return null;
          },
        ),
      } as Column),

      createTableColumn({
        Header: 'File(s)',
        accessor: 'form.fileName',
        Cell: createTableCell<string, any>(
          (value: string, item: IFormFileAttachmentItem, index: number, cellExtras: { [key: string]: any }) => {
            if (hasDownloadRight && item.fileInfo?.id) {
              return (
                <DownloadFile
                  type={EProjectFileCategory.attachments}
                  text={item.fileInfo.fileName}
                  attachmentId={item.fileInfo?.id}
                  projectId={item.fileInfo.projectId}
                />
              );
            }
            return <div>{item.form?.fileName}</div>;
          },
        ),
      } as Column),
    );
    return cols;
  }, [hasUploadRight, hasDownloadRight, mode]);

  const onClickItemDelete = (item: IFormFileAttachmentItem, index: number) => {
    if (item.form?.id && item.form?.projectId)
      mutateDeleteProjectFile({ id: item.form?.id, projectId: item.form?.projectId });
  };

  const onAddedFile = React.useMemo(() => {
    return (addedFile: any, isFromDropzone = true) => {
      if (!addedFile || addedFile === null) return;
      if (isFromDropzone && !addedFile.upload) return;

      const accepted =
        isFromDropzone && acceptFileType && acceptFileType.length > 0 ? acceptFileType.includes(addedFile.type) : true;

      const { filename, name } = isFromDropzone ? addedFile.upload : addedFile;
      const filenameLocal = isFromDropzone ? filename : name;
      const { extension } = getFilenameAndExt(filenameLocal);

      if (!accepted) {
        toastMessage(
          <ToastMessage
            message={`File type not allowed for ${filename}`}
            toastOptions={{ type: 'error', closeOnClick: true, autoClose: false }}
          />,
        );
        return;
      }

      setLocalFilesWithShouldDirty((oldLocalFiles) => {
        const newLocalFiles = [
          ...oldLocalFiles,
          {
            file: addedFile,
            extension,
            form: {
              fileType,
              addT3chop: true,
              projectId,
              uuid: uuidv4(),
              fileName: filenameLocal,
              lastModifiedDate: new Date(),
              ...uploadFileRequestParam,
            },
          },
        ];

        return newLocalFiles as IFormFileAttachmentItem[];
      });
    };
  }, [projectId, JSON.stringify(uploadFileRequestParam)]);

  const onAddedFiles = React.useMemo(() => {
    return (addedFiles: any[]) => {
      addedFiles.forEach((addedFile) => onAddedFile(addedFile));
    };
  }, [JSON.stringify(onAddedFile)]);

  return (
    <div
      onClick={onClick}
      className={`${className} gcl-gems-file-upload-with-display-container`}
      key={`${id}_file_display_container`}
    >
      <ApiErrorDisplay errors={[{ error: errorDeleteProjectFile, genericError: 'Delete Project File Fail' }]} />
      {!hasDownloadRight && !hasUploadRight && !shouldDisplayFileNameWhileNoAccessRight ? (
        <AlertMessage content="No access right to view files" type={ComponentType.Warning} />
      ) : (
        <>
          <FilesDisplay
            defaultIsExpanded={defaultIsExpanded}
            id={`${id}_file_display`}
            key={`${id}_file_display`}
            header={header}
            files={localFiles}
            listViewColumns={attachmentColumns}
            dropZoneUploaderProps={
              mode === 'View' || !hasUploadRight
                ? undefined
                : {
                    eventHandlers: {
                      addedfile: onAddedFile,
                      addedfiles: onAddedFiles,
                    },
                  }
            }
            listViewProps={{
              responsive: false,
              cellExtras: { localFiles, mode, shouldHaveAtLeastOneFile, isAnyRowUploading },
            }}
            onRenderExtraHeaderButtons={(defaultButtons) =>
              mode === 'View' || !hasUploadRight
                ? [...customHeaderButtons]
                : [
                    <span key={`gcl-gems-file-upload-${id}-desc`} className="gcl-mr-xs">
                      Drag and drop or click upload button to upload file
                    </span>,
                    <Tooltip overlay="Upload New File" id="upload-new-file" key="upload-new-file">
                      <label
                        data-testid={`upload-attachments-${id}`}
                        onChange={(e) => {
                          const { files } = e.target as HTMLInputElement;
                          const selectedFile = files?.item(0);
                          onAddedFile(selectedFile, false);
                        }}
                        htmlFor={`gcl-gems-file-upload-${id}-file-input`}
                      >
                        <input
                          name=""
                          type="file"
                          accept={acceptFileType}
                          id={`gcl-gems-file-upload-${id}-file-input`}
                          hidden
                          onClick={(e) => {
                            const element = e.target as HTMLInputElement;
                            element.value = '';
                          }}
                        />
                        <Icon
                          iconName="Upload"
                          className="gcl-files-display-icon-button gcl-fc-primary-400 gcl-ic-btn-lg"
                        />
                      </label>
                    </Tooltip>,
                    ...customHeaderButtons,
                  ]
            }
          />
          <FormErrorDisplay error={controlError} />
        </>
      )}
    </div>
  );
};
